import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Leaf, BookOpen, Sprout, Sun, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const phaseIcons = {
  1: Leaf,
  2: BookOpen,
  3: Sprout,
  4: Sun,
};

const phaseDescriptions = {
  1: "Your nervous system has been through a lot. Right now, the most important thing is safety — not answers. Sage will help you ground, breathe, and find moments of stillness before anything else.",
  2: "You've survived the worst of it. Now comes the part where you start to understand why — and that changes everything. Sage will walk with you through the patterns, the questions, and the moments that finally start to make sense.",
  3: "You know who you were before. Now it's time to decide who you're becoming. Sage will help you rebuild your identity, set boundaries, and reclaim the parts of yourself you thought were lost.",
  4: "The storm is behind you. What's ahead is a life you get to design. Sage will help you step into your power, find your purpose, and live from a place of wholeness.",
};

export const PhaseAssignmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  const result = location.state?.result;
  const [checkingQuiz, setCheckingQuiz] = useState(true);
  const [quizCompleted, setQuizCompleted] = useState(false);

  useEffect(() => {
    const checkQuizStatus = async () => {
      try {
        const response = await axios.get(`${API}/attachment/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuizCompleted(response.data.completed || response.data.skipped);
      } catch {
        // If error, assume not completed
        setQuizCompleted(false);
      } finally {
        setCheckingQuiz(false);
      }
    };
    
    if (token) {
      checkQuizStatus();
    } else {
      setCheckingQuiz(false);
    }
  }, [token]);

  const handleBeginJourney = () => {
    if (quizCompleted) {
      // Skip to checkout if quiz already done
      navigate('/checkout');
    } else {
      // Go to attachment quiz
      navigate('/attachment-quiz');
    }
  };

  if (!result) {
    navigate('/assessment');
    return null;
  }

  if (checkingQuiz) {
    return (
      <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-600" />
      </div>
    );
  }

  const { phase, phase_name } = result;
  const Icon = phaseIcons[phase];
  const description = phaseDescriptions[phase];

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-3xl text-center">
        <div className="mb-8">
          <h1
            className="text-4xl md:text-5xl font-medium tracking-tight text-stone-800 mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="phase-assignment-heading"
          >
            This is where you are
          </h1>
          <p className="text-lg text-stone-600">
            And it's exactly the right place to start.
          </p>
        </div>

        <div
          className="rounded-3xl p-12 md:p-16 mb-8"
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAE4DC' }}
          data-testid="phase-assignment-card"
        >
          <div 
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'rgba(201,168,76,0.15)' }}
          >
            <Icon className="w-10 h-10" style={{ color: '#C9A84C' }} strokeWidth={1.5} />
          </div>

          <div 
            className="text-sm font-medium mb-2"
            style={{ color: '#C9A84C' }}
          >
            PHASE {phase}
          </div>

          <h2
            className="text-3xl md:text-4xl font-medium mb-6 text-[#1A1A2E]"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {phase_name}
          </h2>

          <p 
            className="text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: '#2E2E42' }}
          >
            {description}
          </p>
        </div>

        <div className="bg-white/60 backdrop-blur-md border border-[#EAE4DC] shadow-lg rounded-2xl p-8 mb-8">
          <h3
            className="text-2xl font-medium mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Sage is ready for you
          </h3>
          <p className="text-stone-600 leading-relaxed mb-6">
            From here, Sage shows up every day — with prompts built for where you actually are, space to process what you're carrying, and a path that moves at your pace. No rushing. No performing. Just you, coming back to yourself.
          </p>
        </div>

        <Button
          onClick={handleBeginJourney}
          className="rounded-full px-12 py-6 text-lg bg-stone-800 text-stone-50 hover:bg-stone-700 shadow-lg transition-all duration-300 hover:scale-[1.02]"
          data-testid="begin-journey-button"
        >
          Begin My Journey
        </Button>
      </div>
    </div>
  );
};
